/**
 * O "Salvar" do painel do dado e o que ele diz (E203.F2): o botão com a gravação em curso, a
 * confirmação e a falha com o texto preservado (E200.F3: a falha diz o que fazer).
 */
import { useState } from 'react';

import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';

const t = ptBR.painelDoDado;

export type EstadoDaGravacao = 'ocioso' | 'salvando' | 'salvo' | 'falhou';

/**
 * O que falta para salvar (E207.F7.C01, E211.F1.T05): o Salvar fica ligado e, ao clicar sem o que
 * a revisão exige, o formulário diz o que falta em vez de ficar mudo e apagado.
 */
export function Falta({
  falta,
  estado,
  testId,
}: {
  falta: string | null;
  estado: EstadoDaGravacao;
  testId: string;
}) {
  if (falta === null || estado !== 'ocioso') return null;
  return (
    <p role="alert" className="text-body-sm text-danger-700" data-testid={testId}>
      {falta}
    </p>
  );
}

/**
 * Grava e diz o resultado; `gravar` devolve a mensagem da recusa, ou `null` quando gravou. O que o
 * formulário entrega na hora de salvar vai como argumento (o estado ainda não o tem).
 */
export function useGravacao<T = void>(
  gravar: (entrada: T) => Promise<string | null>,
  depois: () => void,
) {
  const [estado, setEstado] = useState<EstadoDaGravacao>('ocioso');
  const [recusa, setRecusa] = useState<string | null>(null);
  async function salvar(entrada: T): Promise<void> {
    setEstado('salvando');
    setRecusa(null);
    try {
      const motivo = await gravar(entrada);
      if (motivo !== null) {
        setRecusa(motivo);
        setEstado('falhou');
        return;
      }
      setEstado('salvo');
      depois();
    } catch {
      setEstado('falhou');
    }
  }
  return { estado, recusa, salvar };
}

export function BotaoSalvar({
  estado,
  recusa = null,
  desligado = false,
  onSalvar,
  testId,
}: {
  estado: EstadoDaGravacao;
  recusa?: string | null;
  desligado?: boolean;
  onSalvar: () => void;
  testId: string;
}): JSX.Element {
  return (
    <div className="space-y-sc-2">
      <Button
        type="button"
        size="sm"
        onClick={onSalvar}
        loading={estado === 'salvando'}
        disabled={desligado || estado === 'salvando'}
        data-testid={testId}
      >
        {t.salvar}
      </Button>
      {estado === 'salvo' ? (
        <p role="status" className="text-body-sm text-success-700" data-testid="painel-salvo">
          {t.salvo}
        </p>
      ) : null}
      {estado === 'falhou' ? (
        <p role="alert" className="text-body-sm text-danger-700" data-testid="painel-falhou">
          {recusa ?? t.falhou}
        </p>
      ) : null}
    </div>
  );
}
